import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';

import { append } from '../../utils';

import { CosmosComponent, OptionProps } from '../../types';

export interface HorizontalSelectProps extends CosmosComponent {
  options: OptionProps[];
  initialValue?: OptionProps;
  showDefaultOption?: boolean;
  onOptionSelect: (selected: OptionProps) => void;
}

const defaultOption: OptionProps = {
  id: 'defaultOption',
  value: 'All', // Can be taken from props in the future
};

export const HorizontalSelect: FC<HorizontalSelectProps> = ({
  initialValue,
  options,
  showDefaultOption = true,
  onOptionSelect,
  testId,
}) => {
  const [selected, setSelected] = useState<OptionProps>(
    initialValue || defaultOption,
  );

  const formattedOptions = [
    ...(showDefaultOption ? [defaultOption] : []),
    ...options,
  ];

  const selectOption = (option: OptionProps) => {
    setSelected(option);
    onOptionSelect?.(option);
  };

  return (
    <StyledWrapper>
      {formattedOptions.map((option, index) => (
        <StyledOption
          isSelected={selected?.id === option?.id}
          key={option.id}
          role="button"
          onClick={() => selectOption(option)}
          data-test-id={append(testId, index + 1)}
        >
          {option.value}
        </StyledOption>
      ))}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  display: inline-flex;
  height: 40px;
  box-sizing: border-box;
  background: ${({ theme: { colors } }) => colors?.white};
  border: 1px solid ${({ theme: { colors } }) => colors?.borderGray};
  border-radius: 2px;
  font-family: 'Open Sans', sans-serif;
`;

const StyledOption = styled.span<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 0 10px;
  font-size: 14px;
  cursor: pointer;

  &:not(:last-child) {
    border-right: 1px solid ${({ theme: { colors } }) => colors?.borderGray};
  }

  &:hover {
    background-color: ${({ theme: { colors } }) => colors?.backgroundGray};
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${({ theme: { colors } }) =>
        colors?.selectedBackground};
    `}
`;
